import type { AwardLetterLine, AwardLetters } from '@myrewards/contracts';
import { useMutation } from '@tanstack/react-query';
import { useId, useState, type ReactElement } from 'react';
import { reviewLetter } from '../../api/letters';
import { Button, cx, Icon, TextField } from '../../ui';
import { Notice } from '../shared';
import {
  checkSummary,
  firstName,
  letterProblemWords,
  PERSON_STATE,
  personStateOf,
  remakeLabel,
  reviewable,
  reviewWords,
} from './presenter';
import l from './Letters.module.css';
import o from '../Ops.module.css';

type Line = AwardLetterLine;

const TONE = {
  info: l.info,
  waiting: l.waiting,
  done: l.done,
  neutral: l.neutral,
  problem: l.problem,
} as const;

/** One person's state in words with its icon: never colour alone (APP-130). */
export function StateChip({ line }: { line: Line }): ReactElement {
  const st = PERSON_STATE[personStateOf(line)];
  return (
    <span className={cx(l.stateChip, TONE[st.tone])}>
      <Icon name={st.icon} size={14} />
      {st.label}
    </span>
  );
}

/**
 * "Check it against the award": every fact the letter prints beside the award's now, with each
 * difference said in words. A letter signed by someone other than the award's signer says so.
 */
export function CheckAgainstAward({ line }: { line: Line }): ReactElement | null {
  const [open, setOpen] = useState(false);
  const id = useId();
  const rows = line.check ?? [];
  if (line.letter === null) return null;
  const first = firstName(line.person.name);
  return (
    <section className={l.review} aria-labelledby={`${id}-title`}>
      <h3 id={`${id}-title`} className={l.reviewTitle}>
        Check it against the award
      </h3>
      <p className={o.muted}>{checkSummary(line)}</p>
      {rows.length > 0 && (
        <Button
          size="sm"
          variant="ghost"
          aria-expanded={open}
          aria-controls={`${id}-rows`}
          icon={<Icon name={open ? 'chevronUp' : 'chevronDown'} size={14} />}
          onPress={() => {
            setOpen((v) => !v);
          }}
        >
          {open ? `Hide ${first}’s facts` : `Show ${first}’s facts side by side`}
        </Button>
      )}
      {open && (
        <ul id={`${id}-rows`} className={l.checkList} aria-label={`${first}’s letter and award`}>
          <li className={cx(l.checkRow, l.checkHeadRow)} aria-hidden="true">
            <span className={l.checkHead}>Fact</span>
            <span className={l.checkHead}>In the letter</span>
            <span className={l.checkHead}>On the award now</span>
          </li>
          {rows.map((r) => (
            <li key={r.field} className={l.checkRow}>
              <span className={l.checkLabel}>
                {r.label}
                {r.differs && (
                  <span className={l.differs}>
                    <Icon name="alert" size={12} />
                    Differs
                  </span>
                )}
              </span>
              <span className={l.checkValue}>
                <span className={l.cellCaption}>In the letter: </span>
                {r.letter ?? 'Not in the letter'}
              </span>
              <span className={l.checkValue}>
                <span className={l.cellCaption}>On the award now: </span>
                {r.award ?? 'Not on the award'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * Review one person's letter (APP-130 D10/D11): Looks right, or Something's wrong with what's
 * wrong. Encouraged, never required. A letter marked Something's wrong stays here until it's
 * made again or cleared with a reason; both fixes are offered.
 */
export function ReviewPanel({
  letters,
  line,
  onReviewed,
  onRemake,
  remaking,
}: {
  letters: AwardLetters;
  line: Line;
  onReviewed: (next: AwardLetters, said: string) => Promise<void>;
  onRemake: (lineId: string) => void;
  remaking: boolean;
}): ReactElement | null {
  const [mode, setMode] = useState<'idle' | 'wrong' | 'clear'>('idle');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const id = useId();
  const review = useMutation({
    mutationFn: (body: { verdict: 'looks_right' | 'something_wrong' | 'clear'; note?: string }) =>
      reviewLetter(letters.requestId, line.lineId, {
        documentId: line.letter?.documentId ?? '',
        ...body,
      }),
  });
  if (line.letter === null || !reviewable(line)) return null;
  const state = personStateOf(line);
  const first = firstName(line.person.name);
  const canReview = letters.actions.review === true;
  const send = async (
    body: { verdict: 'looks_right' | 'something_wrong' | 'clear'; note?: string },
    said: string,
  ): Promise<void> => {
    setError(null);
    try {
      const next = await review.mutateAsync(body);
      setMode('idle');
      setNote('');
      await onReviewed(next, said);
    } catch (e) {
      setError(letterProblemWords(e));
    }
  };
  return (
    <section className={l.review} aria-labelledby={`${id}-title`}>
      <h3 id={`${id}-title`} className={l.reviewTitle}>
        Review {first}’s letter
      </h3>
      {line.review ? (
        <p className={o.muted}>{reviewWords(line.review)}</p>
      ) : (
        <p className={o.muted}>
          Not reviewed yet. Reading it first is encouraged; it can go out without a review.
        </p>
      )}
      {state === 'something_wrong' && (
        <Notice tone="problem">
          It can’t go out until it’s made again or cleared with a reason.
        </Notice>
      )}
      {canReview && mode === 'idle' && (
        <div className={l.buttons}>
          {state === 'something_wrong' ? (
            <>
              <Button
                variant="navy"
                icon={<Icon name="refresh" size={16} />}
                isPending={remaking}
                onPress={() => {
                  onRemake(line.lineId);
                }}
              >
                {remakeLabel(line)}
              </Button>
              <Button
                variant="ghost"
                onPress={() => {
                  setMode('clear');
                }}
              >
                Clear it with a reason
              </Button>
            </>
          ) : (
            <>
              {state !== 'reviewed' && (
                <Button
                  variant="navy"
                  icon={<Icon name="check" size={16} />}
                  isPending={review.isPending}
                  onPress={() => {
                    void send({ verdict: 'looks_right' }, `${first}’s letter looks right`);
                  }}
                >
                  Looks right
                </Button>
              )}
              <Button
                variant="ghost"
                icon={<Icon name="alert" size={16} />}
                onPress={() => {
                  setMode('wrong');
                }}
              >
                Something’s wrong
              </Button>
            </>
          )}
        </div>
      )}
      {canReview && mode !== 'idle' && (
        <form
          className={o.stack}
          onSubmit={(e) => {
            e.preventDefault();
            if (note.trim() === '') return;
            void send(
              mode === 'wrong'
                ? { verdict: 'something_wrong', note: note.trim() }
                : { verdict: 'clear', note: note.trim() },
              mode === 'wrong'
                ? `${first}’s letter marked Something’s wrong`
                : `${first}’s letter cleared`,
            );
          }}
        >
          <TextField
            label={mode === 'wrong' ? 'What’s wrong with it?' : 'Why can it go as it is?'}
            description={
              mode === 'wrong'
                ? 'Goes in the award’s history. The letter waits until it’s made again or cleared.'
                : 'Goes in the award’s history with the clearing.'
            }
            value={note}
            onChange={setNote}
            multiline
            isRequired
            maxLength={1000}
          />
          <div className={l.buttons}>
            <Button
              type="submit"
              variant="navy"
              isPending={review.isPending}
              isDisabled={note.trim() === ''}
            >
              {mode === 'wrong' ? 'Mark Something’s wrong' : 'Clear it'}
            </Button>
            <Button
              variant="ghost"
              onPress={() => {
                setMode('idle');
                setNote('');
                setError(null);
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
      {error !== null && <Notice tone="problem">{error}</Notice>}
    </section>
  );
}
